import { SolutionScenario } from '@/types/scenarios/solution';
import { Car, Phone, Calendar, Users, Wrench, DollarSign, Bell, FileText } from 'lucide-react';

export const automotive: SolutionScenario = {
  meta: {
    id: 'automotive',
    slug: 'automotive',
    name: 'Automotive',
    tagline: 'Genies that handle the sales floor and the service desk',
    description: "A genie that books test drives and service appointments, answers parts and trade-in questions, and only routes the calls that actually need a salesperson or service advisor.",
    market: 'Automotive',
    subMarkets: ['New Car Dealerships', 'Used Car Dealerships', 'Service Centers', 'Auto Parts', 'Fleet Management'],
    specificNeeds: [
      '24/7 lead capture and qualification',
      'Service appointment scheduling',
      'Inventory inquiries handling',
      'Trade-in value estimates',
      'Test drive scheduling',
      'Service reminder calls'
    ],
    commonRoles: ['Sales Representatives', 'Service Advisors', 'BDC Agents', 'Sales Managers'],
    marketSize: '$2.3 trillion globally',
    isFeatured: true
  },

  hero: {
    headline: 'Sales and service, both handled',
    headlineGradient: 'Sales and service,',
    headlineRest: 'both handled',
    subheadline: "A genie that books test drives and service appointments, answers parts, pricing, and trade-in questions, and only routes the calls that actually need a salesperson or service advisor.",
  },

  recommendedPlaybooks: [
    'lead-capture',
    'appointment-scheduler',
    'demo-scheduler',
    'proactive-welcome'
  ],

  applications: [
    {
      id: 'lead-capture',
      name: 'Lead Capture & Qualification',
      description: 'Capture and qualify leads around the clock, ensuring no opportunity is missed',
      department: 'Sales',
      icon: Phone,
      playbookIds: ['lead-capture', 'proactive-welcome'],
      benefits: [
        'Capture leads 24/7, including after hours',
        'Qualify prospects before handoff to sales',
        'Instant response to web inquiries',
        'Integrate with your CRM automatically'
      ]
    },
    {
      id: 'test-drive-scheduling',
      name: 'Test Drive Scheduling',
      description: 'Allow customers to easily schedule test drives at their convenience',
      department: 'Sales',
      icon: Car,
      playbookIds: ['demo-scheduler'],
      benefits: [
        'Easy self service booking',
        'Automatic availability checking',
        'Confirmation and reminder calls',
        'Reduce no-shows by 35%'
      ]
    },
    {
      id: 'service-appointments',
      name: 'Service Appointment Booking',
      description: 'Streamline service scheduling and increase service department revenue',
      department: 'Service',
      icon: Wrench,
      playbookIds: ['appointment-scheduler'],
      benefits: [
        '24/7 service scheduling',
        'Automated service reminders',
        'Upsell maintenance packages',
        'Reduce call wait times'
      ]
    },
    {
      id: 'inventory-inquiries',
      name: 'Inventory & Pricing Inquiries',
      description: 'Answer questions about vehicle availability, features, and pricing',
      department: 'Sales',
      icon: DollarSign,
      playbookIds: ['interactive-faq'],
      benefits: [
        'Instant inventory information',
        'Feature comparison assistance',
        'Pricing and financing options',
        'Trade-in value estimates'
      ]
    },
    {
      id: 'service-reminders',
      name: 'Service Reminder Campaigns',
      description: 'Proactive outreach for maintenance reminders and recall notifications',
      department: 'Service',
      icon: Bell,
      playbookIds: ['proactive-welcome'],
      benefits: [
        'Automated maintenance reminders',
        'Recall notification calls',
        'Seasonal service promotions',
        'Customer retention boost'
      ]
    },
    {
      id: 'financing-prequalification',
      name: 'Financing Pre-Qualification',
      description: 'Guide customers through initial financing questions and credit applications',
      department: 'Finance',
      icon: FileText,
      playbookIds: ['lead-capture', 'interactive-faq'],
      benefits: [
        'Pre-qualify buyers 24/7',
        'Collect financing applications',
        'Explain payment options',
        'Reduce F&I bottlenecks'
      ]
    }
  ],

  challenges: [
    {
      title: 'Missed After Hours Leads',
      description: 'Most car shoppers research and reach out outside business hours, leading to lost opportunities',
      impact: 'high',
      affectedAreas: ['Sales revenue', 'Market share', 'Customer acquisition']
    },
    {
      title: 'High BDC Staffing Costs',
      description: 'Business Development Centers require significant staffing to handle call volumes',
      impact: 'high',
      affectedAreas: ['Operating costs', 'Profit margins', 'Scalability']
    },
    {
      title: 'Service Department Capacity',
      description: 'Phone lines tied up with scheduling calls prevent focus on customer service',
      impact: 'medium',
      affectedAreas: ['Service revenue', 'Customer satisfaction', 'Staff efficiency']
    },
    {
      title: 'Inconsistent Customer Experience',
      description: 'Variable quality depending on which representative handles the call',
      impact: 'medium',
      affectedAreas: ['Brand perception', 'Customer loyalty', 'Review ratings']
    }
  ],

  solutions: [
    {
      challenge: 'Missed after hours opportunities',
      solution: 'Deploy 24/7 Voice Genie for lead capture and qualification',
      implementation: 'Integrate with existing phone system and CRM, configure lead qualification criteria',
      expectedResults: [
        '40% increase in captured leads',
        'Immediate response to all inquiries',
        'Qualified leads delivered to sales team'
      ]
    },
    {
      challenge: 'High BDC operational costs',
      solution: 'Automate routine inquiries and appointment scheduling',
      implementation: 'Handle common questions and bookings automatically, escalate complex inquiries',
      expectedResults: [
        '60% reduction in routine call handling',
        'Reallocate staff to high-value activities',
        '35% reduction in BDC costs'
      ]
    },
    {
      challenge: 'Service scheduling bottlenecks',
      solution: 'Self service appointment booking with intelligent scheduling',
      implementation: 'Connect to service calendar, enable voice-based booking and reminders',
      expectedResults: [
        '50% reduction in scheduling calls',
        '25% increase in service appointments',
        'Higher customer satisfaction scores'
      ]
    }
  ],

  integrations: {
    marketSpecific: [
      {
        name: 'DealerSocket',
        category: 'CRM',
        description: 'Automotive-specific CRM for lead and customer management',
        required: false,
        popularityScore: 85
      },
      {
        name: 'VinSolutions',
        category: 'CRM',
        description: 'Connected automotive retail platform',
        required: false,
        popularityScore: 82
      },
      {
        name: 'CDK Global',
        category: 'DMS',
        description: 'Dealer management system for inventory and operations',
        required: false,
        popularityScore: 90
      },
      {
        name: 'Reynolds and Reynolds',
        category: 'DMS',
        description: 'Comprehensive dealership management solutions',
        required: false,
        popularityScore: 88
      }
    ],
    common: [
      {
        name: 'Salesforce',
        category: 'CRM',
        description: 'Enterprise CRM platform',
        required: false,
        popularityScore: 95
      },
      {
        name: 'Google Calendar',
        category: 'Scheduling',
        description: 'Calendar integration for appointments',
        required: false,
        popularityScore: 92
      }
    ]
  },

  metrics: {
    averageImplementationTime: '1-2 weeks',
    typicalROI: '420%',
    adoptionRate: '89% customer satisfaction',
    customerSatisfaction: '4.8/5'
  },

  demoConfig: {
    firstMessage: "Hi! Thanks for calling. I can help you find the perfect vehicle, schedule a test drive, book a service appointment, or answer any questions you have. What can I help you with today?",
    systemPrompt: "You are a friendly automotive sales and service assistant. Help customers with vehicle inquiries, schedule test drives and service appointments, and qualify leads. Be knowledgeable about common automotive questions and always aim to convert inquiries into appointments.",
    marketContext: "Automotive dealership handling sales and service inquiries",
    exampleScenarios: [
      "I'm looking for a new SUV with good gas mileage",
      "I need to schedule an oil change",
      "What's my trade-in worth?",
      "Do you have any trucks in stock?"
    ]
  },

  caseStudies: [
    {
      companyName: 'Metro Auto Group',
      companySize: '5 dealerships',
      location: 'Midwest United States',
      challenge: 'Missing 40% of after hours leads and struggling with BDC staffing costs',
      implementation: 'Deployed Help Genie across all locations for 24/7 lead capture and service scheduling',
      timeline: '2 weeks',
      results: [
        {
          metric: 'Lead Capture Rate',
          value: '+45%',
          improvement: 'vs. previous year'
        },
        {
          metric: 'BDC Cost Reduction',
          value: '38%',
          improvement: 'Annual savings'
        },
        {
          metric: 'Service Appointments',
          value: '+28%',
          improvement: 'Monthly increase'
        },
        {
          metric: 'Customer Satisfaction',
          value: '4.7/5',
          improvement: 'Up from 4.1'
        }
      ],
      quote: {
        text: "Help Genie transformed how we handle customer inquiries. We're capturing leads we used to miss and our service department is running more efficiently than ever.",
        author: 'Mike Thompson',
        title: 'General Manager'
      }
    }
  ],

  resources: [
    {
      type: 'guide',
      title: 'Automotive Voice Genie Playbook',
      description: 'Complete guide to implementing voice technology in your dealership',
      url: '/resources/automotive-voice-guide'
    },
    {
      type: 'webinar',
      title: 'Capturing After Hours Leads with Help Genie',
      description: 'Learn how top dealerships increase lead capture by 40%+',
      url: '/webinars/automotive-leads'
    },
    {
      type: 'template',
      title: 'Service Reminder Scripts',
      description: 'Pre-built voice scripts for service department outreach',
      url: '/templates/service-reminders'
    }
  ],

  faqs: [
    {
      question: 'How does the genie handle complex vehicle questions?',
      answer: 'Your genie is trained on automotive terminology and can answer common questions about features, specifications, and availability. For complex negotiations or specific deal discussions, it seamlessly transfers to your sales team.'
    },
    {
      question: 'Can it integrate with our existing DMS?',
      answer: 'Yes, Help Genie integrates with all major dealer management systems including CDK Global, Reynolds and Reynolds, and others. We also support popular CRMs like DealerSocket and VinSolutions.'
    },
    {
      question: 'What happens to leads captured after hours?',
      answer: 'Leads are immediately logged in your CRM with full conversation details. Your team receives instant notifications, and follow-up appointments can be scheduled automatically.'
    }
  ],

  pageContent: {
    scenariosHeader: {
      title: "Shoppers call after hours\nyour Genie closes the deal",
      subtitle: "See how your Genie handles real dealership calls — booking test drives, scheduling service, and qualifying buyers while your sales floor is dark.",
    },
    chatScenarios: [
      {
        id: 'test_drive',
        title: 'Test Drive Booking',
        messages: [
          { type: 'user' as const, text: "I've been looking at the 2024 RAV4 Hybrid online. Do you have any in stock? I'd love to take one for a test drive.", delay: 300 },
          { type: 'genie' as const, text: "Great choice — the RAV4 Hybrid is one of our most popular models! We currently have 4 in stock. Are you looking for a particular trim or color?", delay: 1200 },
          { type: 'user' as const, text: "XLE Premium in blue if you have it", delay: 2500 },
          { type: 'genie' as const, text: "We have a Blueprint XLE Premium on the lot right now. I can schedule your test drive — what day works best for you?", delay: 3400 },
          { type: 'user' as const, text: "Saturday morning would be great. My name is Jennifer Walsh, 555-342-8901", delay: 4600 },
          { type: 'genie' as const, text: "You're booked for Saturday at 10 AM, Jennifer. The Blueprint RAV4 Hybrid XLE Premium will be prepped and ready. Ask for the sales desk when you arrive. See you Saturday!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Vehicle interest captured (RAV4 Hybrid)', delay: 6500 },
          { id: '2', text: 'Trim and color preference logged', delay: 6700 },
          { id: '3', text: 'Inventory matched to request', delay: 6900 },
          { id: '4', text: 'Test drive booked (Saturday 10 AM)', delay: 7100 },
          { id: '5', text: 'Hot lead created for sales team', delay: 7300 },
        ],
      },
      {
        id: 'service_appointment',
        title: 'Service Booking',
        messages: [
          { type: 'user' as const, text: "My check engine light came on this morning. It's a 2021 Camry with about 38,000 miles.", delay: 300 },
          { type: 'genie' as const, text: "Let's get that checked out. A check engine light on a 2021 Camry at 38K miles could be a few things — often emissions-related. Is the light steady or flashing?", delay: 1200 },
          { type: 'user' as const, text: "It's steady, not flashing", delay: 2500 },
          { type: 'genie' as const, text: "Good — steady means it's not critical, but you should get it diagnosed soon. I can schedule a service appointment. We have openings tomorrow at 7:30 AM and 1:00 PM.", delay: 3400 },
          { type: 'user' as const, text: "7:30 works. Do I need a loaner?", delay: 4600 },
          { type: 'genie' as const, text: "Diagnostics usually take 1–2 hours. I'll reserve a loaner just in case it needs more time. You're booked for tomorrow at 7:30 AM. What's your name?", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Vehicle and mileage documented', delay: 6500 },
          { id: '2', text: 'Symptom assessed (steady CEL)', delay: 6700 },
          { id: '3', text: 'Service appointment booked', delay: 6900 },
          { id: '4', text: 'Loaner vehicle reserved', delay: 7100 },
          { id: '5', text: 'Service revenue captured', delay: 7300 },
        ],
      },
      {
        id: 'trade_in',
        title: 'Trade-In Inquiry',
        messages: [
          { type: 'user' as const, text: "I want to trade in my 2020 Honda Accord. What's it worth? It has 45,000 miles, good condition.", delay: 300 },
          { type: 'genie' as const, text: "A 2020 Accord with 45K miles in good condition is a great trade! Is it the Sport, EX, or Touring trim? And is it the 1.5T or 2.0T engine?", delay: 1200 },
          { type: 'user' as const, text: "EX-L with the 1.5 turbo. No accidents, one owner.", delay: 2500 },
          { type: 'genie' as const, text: "One-owner, clean history EX-L — those hold value well. Based on current market data, you're likely looking at $22,000–$24,000 range. For an exact appraisal, I can schedule a 15-minute in-person evaluation.", delay: 3400 },
          { type: 'user' as const, text: "That's in the range I was hoping. Can I come by this week?", delay: 4600 },
          { type: 'genie' as const, text: "Absolutely! We can do the appraisal and show you what we have in your next vehicle at the same time. When works best? I have Wednesday and Thursday afternoon open.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Trade-in details captured', delay: 6500 },
          { id: '2', text: 'Estimated value provided', delay: 6700 },
          { id: '3', text: 'Appraisal visit scheduled', delay: 6900 },
          { id: '4', text: 'New vehicle interest identified', delay: 7100 },
          { id: '5', text: 'Dual opportunity (sell + buy)', delay: 7300 },
        ],
      },
      {
        id: 'inventory_check',
        title: 'Inventory Inquiry',
        messages: [
          { type: 'user' as const, text: "Do you have any trucks with under 30,000 miles? Looking for a crew cab, preferably a Tacoma or Tundra.", delay: 300 },
          { type: 'genie' as const, text: "Let me check our truck inventory. We have a 2023 Tacoma TRD Sport with 18K miles and a 2022 Tundra SR5 CrewMax with 26K miles. Both are crew cabs. Any preference between the two?", delay: 1200 },
          { type: 'user' as const, text: "Tell me more about the Tundra. What color and price?", delay: 2500 },
          { type: 'genie' as const, text: "The Tundra is Midnight Black Metallic, SR5 CrewMax with the 3.5L twin-turbo V6. Priced at $44,900. It's got tow package, running boards, and a bed liner. Would you like to come see it?", delay: 3400 },
          { type: 'user' as const, text: "Yeah, I'm interested. What's available this weekend?", delay: 4600 },
          { type: 'genie' as const, text: "I can hold the Tundra for you. Saturday morning or afternoon — what works? I'll have it pulled up front and ready.", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'Vehicle preferences captured', delay: 6500 },
          { id: '2', text: 'Matching inventory presented', delay: 6700 },
          { id: '3', text: 'Pricing and features provided', delay: 6900 },
          { id: '4', text: 'Showroom visit scheduled', delay: 7100 },
          { id: '5', text: 'Vehicle held for customer', delay: 7300 },
        ],
      },
      {
        id: 'after_hours_lead',
        title: 'After Hours',
        messages: [
          { type: 'user' as const, text: "Hi, I know it's late but I just saw a great price on a Corolla Cross on your website. Is that still available?", delay: 300 },
          { type: 'genie' as const, text: "No problem at all — I'm here 24/7! Let me look that up. Which Corolla Cross are you seeing? If you have the stock number, that helps.", delay: 1200 },
          { type: 'user' as const, text: "Stock number TC4421, it says $28,500", delay: 2500 },
          { type: 'genie' as const, text: "Got it — stock TC4421 is a 2024 Corolla Cross XLE in Wind Chill Pearl at $28,500. As of close today, it's still available. Want to be the first appointment tomorrow to see it?", delay: 3400 },
          { type: 'user' as const, text: "Yes! First thing in the morning. I'm Mark Chen, 555-671-0023", delay: 4600 },
          { type: 'genie' as const, text: "You're booked first thing, Mark. I'll flag this vehicle so it's ready for you and let the sales team know you're coming. They open at 9 AM — see you then!", delay: 5500 },
        ],
        outcomes: [
          { id: '1', text: 'After hours website lead captured', delay: 6500 },
          { id: '2', text: 'Specific vehicle interest confirmed', delay: 6700 },
          { id: '3', text: 'Availability verified', delay: 6900 },
          { id: '4', text: 'First appointment booked', delay: 7100 },
          { id: '5', text: 'Vehicle flagged for sales team', delay: 7300 },
        ],
      },
    ],
    useCasesHeader: {
      title: "Stop Losing Sales to Voicemail",
      subtitle: "70% of car shoppers research after hours. When they call your dealership at 9 PM, your Genie books the test drive before a competitor can.",
    },
    useCaseCards: [
      {
        id: 'lead-capture',
        title: '24/7 Lead Capture',
        description: 'Never miss an after hours inquiry. Your Genie captures and qualifies leads around the clock.',
        bullets: [
          'Capture leads outside business hours',
          'Qualify prospects automatically',
          'Instant notifications to your team',
          'Never lose another opportunity'
        ],
        image: '/solutions-pages/automotive/usecase-lead-capture.webp'
      },
      {
        id: 'test-drives',
        title: 'Test Drive Scheduling',
        description: 'Let customers book test drives at their convenience, any time of day.',
        bullets: [
          'Easy self service booking',
          'Automatic availability checking',
          'Confirmation and reminder calls',
          'Reduce no-shows by 35%'
        ],
        image: '/solutions-pages/automotive/usecase-test-drives.webp'
      },
      {
        id: 'service-appointments',
        title: 'Service Appointments',
        description: 'Streamline service scheduling and keep your bays full.',
        bullets: [
          '24/7 service scheduling',
          'Automated service reminders',
          'Upsell maintenance packages',
          'Reduce call wait times'
        ],
        image: '/solutions-pages/automotive/usecase-service.webp'
      },
      {
        id: 'financing',
        title: 'Financing Pre-Qualification',
        description: 'Guide customers through initial financing questions before they arrive.',
        bullets: [
          'Pre-qualify buyers 24/7',
          'Collect financing applications',
          'Explain payment options',
          'Reduce F&I bottlenecks'
        ],
        image: '/solutions-pages/automotive/usecase-financing.webp'
      }
    ],
    finalCTA: {
      title: 'Your Dealership Never Sleeps',
      subtitle: 'While you rest, your Genie captures every lead, schedules every appointment, and ensures no opportunity slips through the cracks.',
      pillars: [
        {
          title: 'Always\nOn',
          gradient: 'from-purple-400 to-blue-400',
          description: 'Capture every inquiry, even at 2am when competitors go to voicemail.'
        },
        {
          title: 'Always\nReady',
          gradient: 'from-blue-400 to-cyan-400',
          description: 'Schedule test drives and service appointments around the clock.'
        },
        {
          title: 'Always\nYou',
          gradient: 'from-cyan-400 to-teal-400',
          description: 'Your voice, your brand — customers never know the difference.'
        }
      ],
      backgroundImage: '/solutions-pages/automotive/cta-background.webp'
    }
  }
};
